// FILE: src/pages/NursingHomeResidentDrawer.jsx
import React, { useEffect, useState, useCallback } from "react";
import { buildPdf } from "../lib/pdfBuilder";
import { getAuth } from "firebase/auth";

const FN_BASE = "https://us-central1-lastwishbox-business.cloudfunctions.net";

const B = {
  red: "#980000",
  redDark: "#650202",
  orange: "#f15822",
  navy: "#003353",
  steel: "#657899",
  bg: "#f6f7fb",
  white: "#fff",
  border: "#e8ebf3",
};

const RESIDENT_LABELS = {
  preferredName: "Preferred name",
  nativeLanguage: "Native language",
  religion: "Religion",
  culturalBackground: "Cultural background",
  meaningfulMusic: "Meaningful music",
  meaningfulObjects: "Meaningful objects",
  importantLifeEvents: "Important life events",
};

const HOME_LABELS = {
  roomNumber: "Room number",
  admissionDateMs: "Admission date",
  keyWorker: "Key worker",
  dietaryRequirements: "Dietary requirements",
  gpName: "GP name",
  gpPhone: "GP phone",
  gpAddress: "GP address",
  nokName: "Next of kin - name",
  nokRelationship: "Next of kin - relationship",
  nokPhone: "Next of kin - phone",
  nokEmail: "Next of kin - email",
  familyInvolvement: "Family involvement",
};

const ATTENDEE_OPTIONS = ["Resident", "Family member", "Key worker", "Advocate", "GP"];

const OUTCOME_OPTIONS = [
  { v: "unchanged", label: "Reviewed - no changes" },
  { v: "updated", label: "Reviewed - wishes updated" },
  { v: "declined_to_participate", label: "Resident declined to take part" },
];

const AUDIT_LABELS = {
  consent_recorded: "Consent recorded",
  profile_viewed: "Record opened",
  wishes_updated: "Care details updated",
  summary_pdf_downloaded: "Summary PDF downloaded",
  review_logged: "Wishes review logged",
};

const ROLE_LABELS = {
  nh_admin: "Home staff",
  system: "LastWishBox admin",
  resident: "Resident",
};

const LONG_FIELDS = ["dietaryRequirements", "gpAddress", "meaningfulMusic", "meaningfulObjects", "importantLifeEvents"];

const FAMILY_OPTIONS = [
  { v: "", label: "Select\u2026" },
  { v: "registered", label: "Registered on LastWishBox" },
  { v: "not_registered", label: "Not registered" },
  { v: "deceased", label: "Deceased" },
  { v: "unknown", label: "Unknown" },
];

function fmtDate(ms) {
  if (!ms) return "";
  try { return new Date(Number(ms)).toLocaleDateString("en-IE"); } catch { return ""; }
}
function fmtDateTime(ms) {
  if (!ms) return "";
  try { return new Date(Number(ms)).toLocaleString("en-IE"); } catch { return ""; }
}
function toDateInput(ms) {
  if (!ms) return "";
  try { return new Date(Number(ms)).toISOString().slice(0, 10); } catch { return ""; }
}

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 10,
  border: `1px solid ${B.border}`,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: B.redDark, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: B.steel, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function ReadOnlyValue({ value }) {
  if (!value) {
    return (
      <div style={{
        padding: "9px 12px", borderRadius: 10, background: B.bg,
        border: `1px dashed ${B.border}`, fontSize: 13, color: B.steel, fontStyle: "italic",
      }}>
        Not recorded - only the resident can complete this
      </div>
    );
  }
  return (
    <div style={{
      padding: "9px 12px", borderRadius: 10, background: B.bg,
      border: `1px solid ${B.border}`, fontSize: 14, color: B.navy, whiteSpace: "pre-wrap",
    }}>{value}</div>
  );
}

export default function NursingHomeResidentDrawer({ tenantId, resident, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [profile, setProfile] = useState({});
  const [form, setForm] = useState({});
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("home");
  const [reviews, setReviews] = useState([]);
  const [logging, setLogging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [consentRec, setConsentRec] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditTruncated, setAuditTruncated] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const [review, setReview] = useState({
    reviewDate: new Date().toISOString().slice(0, 10),
    conductedBy: "",
    attendees: ["Resident"],
    outcome: "unchanged",
    notes: "",
  });

  const residentUid = resident?.uid;

  // The dashboard already holds each resident's review dates and consent state.
  // Seed from that so the drawer has content on the first paint.
  const seedFromRow = resident || {};

  const load = useCallback(async () => {
    if (!tenantId || !residentUid) return;
    setLoading(true);
    setErr("");
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const token = await user.getIdToken();

      const r = await fetch(
        `${FN_BASE}/nhGetProfile?tenantId=${encodeURIComponent(tenantId)}&residentUid=${encodeURIComponent(residentUid)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);

      const p = data.profile || {};
      setProfile(p);
      const f = {};
      Object.keys(HOME_LABELS).forEach((k) => { f[k] = p[k] == null ? "" : p[k]; });
      setForm(f);
      setHistory(Array.isArray(data.fieldHistory) ? data.fieldHistory : []);

      // PERF: reviews now arrive with the profile in a single request. The
      // separate nhListReviews call was a second round trip to us-central1 that
      // repeated the same permission reads.
      setReviews(Array.isArray(data.reviews) ? data.reviews : []);
      setConsentRec(data.nhConsent || null);
    } catch (e) {
      console.error("nhGetProfile failed", e);
      setErr(String(e.message || e));
    }
    setLoading(false);
  }, [tenantId, residentUid]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Long form dates for the PDF. The screen keeps the short format.
  // Loaded on demand: most visits never open this tab, and the audit trail
  // grows with every action on the record.
  const loadAudit = useCallback(async () => {
    if (!tenantId || !residentUid) return;
    setAuditLoading(true);
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const token = await user.getIdToken();
      const r = await fetch(
        `${FN_BASE}/nhGetAuditTrail?tenantId=${encodeURIComponent(tenantId)}&residentUid=${encodeURIComponent(residentUid)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
      setAudit(Array.isArray(data.entries) ? data.entries : []);
      setAuditTruncated(data.truncated === true);
      setAuditLoaded(true);
    } catch (e) {
      console.error("nhGetAuditTrail failed", e);
      setErr(String(e.message || e));
    }
    setAuditLoading(false);
  }, [tenantId, residentUid]);

  useEffect(() => {
    if (tab === "audit" && !auditLoaded && !auditLoading) loadAudit();
  }, [tab, auditLoaded, auditLoading, loadAudit]);

  // Audit times: no seconds, and a month name so 3/9 is never ambiguous.
  const auditTime = (ms) => {
    const n = Number(ms);
    if (!n) return "";
    return new Date(n).toLocaleString("en-IE", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  };

  const cap = (s) => {
    const t = String(s || "").trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
  };

  const pdfDate = (ms) => {
    const n = Number(ms);
    if (!n) return "";
    return new Date(n).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" });
  };

  const downloadSummary = async () => {
    setDownloading(true);
    setErr("");
    setOk("");
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const token = await user.getIdToken();

      // Server-side: records the download in the HIQA audit trail and returns
      // the home name and the actor name. Nothing is generated if this fails.
      const r = await fetch(`${FN_BASE}/nhLogSummaryDownload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, residentUid }),
      });
      const meta = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(meta.error || `Request failed (${r.status})`);

      const consent = consentRec || {};
      const special = consent.specialCategoryConsent === true;
      const givenBy = consent.consentGivenBy || "";

      const careRows = Object.keys(HOME_LABELS).map((k) => {
        if (k === "admissionDateMs") return [HOME_LABELS[k], pdfDate(profile[k])];
        if (k === "familyInvolvement") {
          const opt = FAMILY_OPTIONS.find((o) => o.v === profile[k]);
          return [HOME_LABELS[k], opt && opt.v ? opt.label : ""];
        }
        return [HOME_LABELS[k], profile[k]];
      });

      // Religion and cultural background are GDPR Article 9 special category
      // data. They only appear if special category consent is recorded.
      const SPECIAL_FIELDS = ["religion", "culturalBackground"];
      const prefRows = Object.keys(RESIDENT_LABELS).map((k) => {
        if (SPECIAL_FIELDS.includes(k) && !special) {
          return [RESIDENT_LABELS[k], "Not shared - no special category consent recorded"];
        }
        return [RESIDENT_LABELS[k], profile[k]];
      });

      const sectionLabels = {
        funeralPlanning: "Funeral planning",
        organDonation: "Organ donation",
        advanceHealthcareDirective: "Advance healthcare directive",
        personalMessages: "Personal messages",
        documents: "Documents",
        contacts: "Contacts",
      };
      // Every section is listed with Yes or No. Naming only the granted ones
      // leaves an inspector unable to tell "not shared" from "not asked".
      const sectionRows = Object.keys(sectionLabels).map((k) => [
        sectionLabels[k],
        consent[k] === true ? "Yes" : "No",
      ]);
      const grantedCount = Object.keys(sectionLabels).filter((k) => consent[k] === true).length;

      const consentRows = [
        ["Consent recorded", pdfDate(consent.consentDateMs)],
        ["Method", cap(consent.consentMethod)],
        ["Given by", cap(givenBy.replace(/_/g, " "))],
      ];
      if (givenBy && givenBy !== "resident") {
        consentRows.push(["Representative", consent.representativeName || ""]);
        consentRows.push(["Authority", consent.representativeAuthority || ""]);
        consentRows.push(["Capacity assessed", pdfDate(consent.capacityAssessedDateMs)]);
      }
      consentRows.push(["Special category consent", special ? "Yes" : "No"]);
      consentRows.push(["Sections shared", `${grantedCount} of ${Object.keys(sectionLabels).length}`]);
      consentRows.push(["Recorded by", consent.consentRecordedByName || ""]);

      const reviewRows = [
        ["Last review", pdfDate(profile.lastReviewDate || resident?.lastReviewDateMs)],
        ["Next review due", pdfDate(profile.nextReviewDate || resident?.nextReviewDateMs)],
      ];
      (reviews || []).slice(0, 5).forEach((rv, i) => {
        const outcome = (OUTCOME_OPTIONS.find((o) => o.v === rv.outcome) || {}).label || rv.outcome || "";
        reviewRows.push([
          `Review ${i + 1}`,
          `${pdfDate(rv.reviewDateMs)} - ${outcome}${rv.conductedBy ? `, by ${rv.conductedBy}` : ""}${rv.notes ? `. ${rv.notes}` : ""}`,
        ]);
      });

      await buildPdf({
        headerTitle: meta.homeName || "Nursing home",
        docTitle: "Resident Wishes Summary",
        subjectLabel: "Resident",
        subjectName: resident?.name || profile?.preferredName || resident?.email || "",
        generatedByName: meta.generatedByName || "",
        fileNamePrefix: "resident-summary",
        signOff: true,
        confidentialNote: "Confidential - contains special category personal data",
        sections: [
          { heading: "Care details", rows: careRows },
          {
            heading: "Resident preferences",
            note: "Completed by the resident. Blank fields have not yet been recorded.",
            rows: prefRows,
          },
          { heading: "Consent", rows: consentRows, keepTogether: true },
          {
            heading: "Information shared with the home",
            note: "Recorded at the time consent was taken.",
            rows: sectionRows,
            keepTogether: true,
          },
          { heading: "Wishes reviews", rows: reviewRows },
        ],
      });

      setOk("Summary downloaded. The download has been recorded in the audit trail.");
    } catch (e) {
      console.error("downloadSummary failed", e);
      setErr(String(e.message || e));
    }
    setDownloading(false);
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    setOk("");
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const fields = {};
      Object.keys(HOME_LABELS).forEach((k) => {
        if (k === "admissionDateMs") {
          fields[k] = form[k] ? Number(form[k]) : null;
        } else {
          fields[k] = form[k] == null ? "" : form[k];
        }
      });

      const r = await fetch(`${FN_BASE}/nhSaveProfile`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, residentUid, fields }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);

      setProfile(data.profile || {});
      const n = (data.changed || []).length;
      setOk(n ? `Saved ${n} change${n > 1 ? "s" : ""}.` : "No changes to save.");
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      console.error("nhSaveProfile failed", e);
      setErr(String(e.message || e));
    }
    setSaving(false);
  };

  const submitReview = async () => {
    setLogging(true);
    setErr("");
    setOk("");
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const reviewDateMs = review.reviewDate ? new Date(review.reviewDate).getTime() : Date.now();

      const r = await fetch(`${FN_BASE}/nhLogReview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId,
          residentUid,
          reviewDateMs,
          conductedBy: review.conductedBy,
          attendees: review.attendees,
          outcome: review.outcome,
          notes: review.notes,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);

      setOk("Review logged.");
      setShowLogForm(false);
      setReview({
        reviewDate: new Date().toISOString().slice(0, 10),
        conductedBy: "",
        attendees: ["Resident"],
        outcome: "unchanged",
        notes: "",
      });
      await load();
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      console.error("nhLogReview failed", e);
      setErr(String(e.message || e));
    }
    setLogging(false);
  };

  const toggleAttendee = (a) =>
    setReview((s) => ({
      ...s,
      attendees: s.attendees.includes(a) ? s.attendees.filter((x) => x !== a) : [...s.attendees, a],
    }));

  const tabs = [
    { key: "home", label: "Care details" },
    { key: "resident", label: "Resident preferences" },
    { key: "reviews", label: "Reviews" },
    { key: "history", label: "Change history" },
    { key: "audit", label: "Audit" },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", top: 60, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,.35)",
        zIndex: 900, display: "flex", justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100%)", height: "100%", background: B.white,
          maxHeight: "100%",
          boxShadow: "-4px 0 24px rgba(0,0,0,.12)", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 19, color: B.redDark }}>{resident?.name || resident?.email || "Resident"}</h2>
            <div style={{ fontSize: 13, color: B.steel, marginTop: 3 }}>{resident?.email || ""}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={downloadSummary}
              disabled={downloading || loading}
              style={{
                border: `1px solid ${B.border}`, background: "#fff", borderRadius: 8,
                padding: "7px 12px", fontSize: 12, fontWeight: 700,
                color: downloading || loading ? B.steel : B.redDark,
                cursor: downloading || loading ? "default" : "pointer", whiteSpace: "nowrap",
              }}
            >{downloading ? "Preparing\u2026" : "Download summary"}</button>
          <button
            onClick={onClose}
            style={{ border: "none", background: "none", fontSize: 26, lineHeight: 1, color: B.steel, cursor: "pointer", padding: 0 }}
            aria-label="Close"
          >&times;</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, padding: "0 22px", borderBottom: `1px solid ${B.border}` }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "11px 12px", border: "none", background: "none",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
                color: tab === t.key ? B.orange : B.steel,
                borderBottom: tab === t.key ? `3px solid ${B.orange}` : "3px solid transparent",
                marginBottom: -1,
              }}
            >{t.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>

          {err && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{err}</div>
          )}
          {ok && (
            <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{ok}</div>
          )}

          {tab === "audit" && (
            <div>
              <div style={{ fontSize: 13, color: B.steel, marginBottom: 14, lineHeight: 1.5 }}>
                Every action taken on this resident&rsquo;s record, newest first.
                Written automatically and cannot be edited or deleted.
              </div>

              {auditLoading ? (
                <div style={{ color: B.steel, padding: 20 }}>Loading audit trail&hellip;</div>
              ) : audit.length === 0 ? (
                <div style={{ color: B.steel, padding: 20 }}>No entries recorded yet.</div>
              ) : (
                <>
                  {audit.map((a) => (
                    <div
                      key={a.id}
                      style={{
                        borderLeft: `3px solid ${B.orange}`,
                        background: "#fafbfc",
                        borderRadius: "0 8px 8px 0",
                        padding: "10px 14px",
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: B.redDark }}>
                          {AUDIT_LABELS[a.action] || a.action}
                        </div>
                        <div style={{ fontSize: 12, color: B.steel, whiteSpace: "nowrap" }}>
                          {a.atMs ? auditTime(a.atMs) : ""}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: B.steel, marginTop: 4 }}>
                        {a.performedByName || "Unknown"}
                        {a.performedByRole ? ` \u00b7 ${ROLE_LABELS[a.performedByRole] || a.performedByRole}` : ""}
                      </div>
                      {a.details && (
                        <div style={{ fontSize: 12.5, color: "#444", marginTop: 6, lineHeight: 1.45 }}>{a.details}</div>
                      )}
                    </div>
                  ))}
                  {auditTruncated && (
                    <div style={{ fontSize: 12, color: B.steel, marginTop: 10 }}>
                      Showing the most recent 200 entries.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {loading ? (
            <div style={{ color: B.steel, padding: 20 }}>Loading profile&hellip;</div>
          ) : tab === "home" ? (
            <>
              {Object.keys(HOME_LABELS).map((k) => (
                <Field key={k} label={HOME_LABELS[k]}>
                  {k === "admissionDateMs" ? (
                    <input
                      type="date"
                      style={inputStyle}
                      value={toDateInput(form[k])}
                      onChange={(e) => setF(k, e.target.value ? new Date(e.target.value).getTime() : "")}
                    />
                  ) : k === "familyInvolvement" ? (
                    <select style={inputStyle} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)}>
                      {FAMILY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  ) : LONG_FIELDS.includes(k) ? (
                    <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} />
                  ) : (
                    <input style={inputStyle} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} />
                  )}
                </Field>
              ))}
            </>
          ) : tab === "resident" ? (
            <>
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                These are the resident&rsquo;s own preferences. Only the resident can enter or change them, from their own login.
              </div>
              {Object.keys(RESIDENT_LABELS).map((k) => (
                <Field key={k} label={RESIDENT_LABELS[k]}>
                  <ReadOnlyValue value={profile[k]} />
                </Field>
              ))}
            </>
          ) : tab === "reviews" ? (
            <>
              {!showLogForm && (
                <button
                  onClick={() => setShowLogForm(true)}
                  style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: B.orange, color: B.white, fontWeight: 700, fontSize: 13, cursor: "pointer", marginBottom: 18 }}
                >Log a review</button>
              )}

              {showLogForm && (
                <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, padding: 16, marginBottom: 20, background: B.bg }}>
                  <Field label="Review date">
                    <input type="date" style={inputStyle} value={review.reviewDate}
                      onChange={(e) => setReview((s) => ({ ...s, reviewDate: e.target.value }))} />
                  </Field>

                  <Field label="Conducted by" hint="Name of the staff member carrying out the review.">
                    <input style={inputStyle} value={review.conductedBy}
                      onChange={(e) => setReview((s) => ({ ...s, conductedBy: e.target.value }))} />
                  </Field>

                  <Field label="Who was present">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {ATTENDEE_OPTIONS.map((a) => {
                        const on = review.attendees.includes(a);
                        return (
                          <button key={a} onClick={() => toggleAttendee(a)}
                            style={{
                              padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
                              border: `1px solid ${on ? B.orange : B.border}`,
                              background: on ? "#fff7ed" : B.white,
                              color: on ? B.orange : B.steel,
                            }}>{a}</button>
                        );
                      })}
                    </div>
                  </Field>

                  <Field label="Outcome">
                    <select style={inputStyle} value={review.outcome}
                      onChange={(e) => setReview((s) => ({ ...s, outcome: e.target.value }))}>
                      {OUTCOME_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </Field>

                  <Field label="Notes" hint="Next review will be scheduled 12 months from the review date.">
                    <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={review.notes}
                      onChange={(e) => setReview((s) => ({ ...s, notes: e.target.value }))} />
                  </Field>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button onClick={() => setShowLogForm(false)}
                      style={{ padding: "8px 16px", borderRadius: 10, border: `1px solid ${B.border}`, background: B.white, color: B.steel, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                    >Cancel</button>
                    <button onClick={submitReview} disabled={logging || !review.conductedBy.trim()}
                      style={{
                        padding: "8px 18px", borderRadius: 10, border: "none", background: B.orange, color: B.white,
                        fontWeight: 700, fontSize: 13,
                        cursor: logging || !review.conductedBy.trim() ? "default" : "pointer",
                        opacity: logging || !review.conductedBy.trim() ? .6 : 1,
                      }}>{logging ? "Saving\u2026" : "Save review"}</button>
                  </div>
                </div>
              )}

              {reviews.length === 0 ? (
                <div style={{ color: B.steel, fontSize: 13 }}>
                  {loading
                    ? (seedFromRow.lastReviewDateMs
                        ? `Last review ${fmtDate(seedFromRow.lastReviewDateMs)}. Loading full history\u2026`
                        : "Loading\u2026")
                    : "No reviews logged yet."}
                </div>
              ) : (
                reviews.map((rv) => {
                  const label = (OUTCOME_OPTIONS.find((o) => o.v === rv.outcome) || {}).label || rv.outcome;
                  const declined = rv.outcome === "declined_to_participate";
                  return (
                    <div key={rv.id} style={{ borderLeft: `3px solid ${declined ? B.steel : B.orange}`, paddingLeft: 12, marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: B.redDark }}>{fmtDate(rv.reviewDateMs)}</div>
                      <div style={{ fontSize: 12, color: declined ? B.steel : B.navy, marginTop: 2, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>
                        By {rv.conductedBy}{rv.attendees?.length ? ` \u00b7 Present: ${rv.attendees.join(", ")}` : ""}
                      </div>
                      {rv.notes && <div style={{ fontSize: 12, color: B.navy, marginTop: 4, whiteSpace: "pre-wrap" }}>{rv.notes}</div>}
                      <div style={{ fontSize: 11, color: B.steel, marginTop: 3 }}>Next due {fmtDate(rv.nextReviewDateMs)}</div>
                    </div>
                  );
                })
              )}
            </>
          ) : tab === "history" ? (
            <>
              {profile.updatedAtMs && (
                <div style={{ fontSize: 12, color: B.steel, marginBottom: 14 }}>
                  Last updated {fmtDateTime(profile.updatedAtMs)}
                  {profile.updatedByName ? ` by ${profile.updatedByName}` : ""}
                </div>
              )}
              {history.length === 0 ? (
                <div style={{ color: B.steel, fontSize: 13 }}>
                  No change history to show yet. Changes made from this panel are recorded against each field.
                </div>
              ) : (
                history.map((h, i) => (
                  <div key={i} style={{ borderLeft: `3px solid ${B.orange}`, paddingLeft: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: B.redDark }}>
                      {HOME_LABELS[h.field] || RESIDENT_LABELS[h.field] || h.field}
                    </div>
                    <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>
                      {h.from ? `"${h.from}"` : "(blank)"} &rarr; {h.to ? `"${h.to}"` : "(blank)"}
                    </div>
                    <div style={{ fontSize: 11, color: B.steel, marginTop: 3 }}>
                      {h.byName || h.byUid} &middot; {fmtDateTime(h.atMs)}
                    </div>
                  </div>
                ))
              )}
            </>
          ) : null}
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${B.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            style={{ padding: "9px 18px", borderRadius: 10, border: `1px solid ${B.border}`, background: B.white, color: B.steel, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
          >Close</button>
          {tab === "home" && (
            <button
              onClick={save}
              disabled={saving || loading}
              style={{ padding: "9px 20px", borderRadius: 10, border: "none", background: B.orange, color: B.white, fontWeight: 700, fontSize: 13, cursor: saving ? "default" : "pointer", opacity: saving ? .7 : 1 }}
            >{saving ? "Saving\u2026" : "Save changes"}</button>
          )}
        </div>
      </div>
    </div>
  );
}
